/** In-memory stale-while-revalidate cache for list screens (survives route unmount). */

interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

const store = new Map<string, CacheEntry<unknown>>();

export function getCached<T>(key: string): T | undefined {
  return store.get(key)?.data as T | undefined;
}

export function setCached<T>(key: string, data: T): void {
  store.set(key, { data, fetchedAt: Date.now() });
}

export function getCachedFetchedAt(key: string): number | undefined {
  return store.get(key)?.fetchedAt;
}

export function isStale(key: string, ttlMs: number): boolean {
  const fetchedAt = getCachedFetchedAt(key);
  if (fetchedAt === undefined) return true;
  return Date.now() - fetchedAt > ttlMs;
}

export function hasCached(key: string): boolean {
  return store.has(key);
}

export function clearCached(key: string): void {
  store.delete(key);
}

export function invalidateListCaches(...keys: string[]): void {
  for (const key of keys) {
    clearCached(key);
  }
}

export function invalidateWorkspaceListCaches(
  workspaceId: string,
  userId?: string,
): void {
  const keys = [
    draftsListCacheKey(workspaceId),
    casesListCacheKey(workspaceId, false),
    casesListCacheKey(workspaceId, true),
    uploadsListCacheKey(workspaceId),
    dashboardListCacheKey(workspaceId),
  ];
  if (userId) {
    keys.push(
      diaryListCacheKey(workspaceId, userId),
      analyticsListCacheKey(workspaceId, userId),
    );
  }
  invalidateListCaches(...keys);
}

export function invalidateOnDraftMutation(workspaceId: string): void {
  invalidateListCaches(
    draftsListCacheKey(workspaceId),
    dashboardListCacheKey(workspaceId),
  );
}

export function invalidateOnCaseMutation(
  workspaceId: string,
  userId?: string,
): void {
  const keys = [
    casesListCacheKey(workspaceId, false),
    casesListCacheKey(workspaceId, true),
    dashboardListCacheKey(workspaceId),
    draftsListCacheKey(workspaceId),
  ];
  if (userId) {
    keys.push(
      diaryListCacheKey(workspaceId, userId),
      analyticsListCacheKey(workspaceId, userId),
    );
  }
  invalidateListCaches(...keys);
}

export function invalidateOnUploadMutation(workspaceId: string): void {
  invalidateListCaches(uploadsListCacheKey(workspaceId));
}

/** Cache keys for list screens */
export function draftsListCacheKey(workspaceId: string): string {
  return `drafts:${workspaceId}`;
}

export function casesListCacheKey(workspaceId: string, closedOnly: boolean): string {
  return `cases:${workspaceId}:${closedOnly ? 'closed' : 'open'}`;
}

export function uploadsListCacheKey(workspaceId: string): string {
  return `uploads:${workspaceId}`;
}

export function dashboardListCacheKey(workspaceId: string): string {
  return `dashboard:${workspaceId}`;
}

export function dashboardProceedingsCacheKey(
  workspaceId: string,
  dateStr: string,
): string {
  return `dashboard:proceedings:${workspaceId}:${dateStr}`;
}

export function diaryListCacheKey(workspaceId: string, userId: string): string {
  return `diary:${workspaceId}:${userId}`;
}

export function analyticsListCacheKey(workspaceId: string, userId: string): string {
  return `analytics:${workspaceId}:${userId}`;
}

export function statutesListCacheKey(): string {
  return 'statutes:all';
}

export const LIST_CACHE_TTL_MS = 30_000;
